import React from 'react';
import { Bar } from 'react-chartjs-2';
import { barDefaults } from '../../utils/chartDefaults';
import {
    deepMerge,
    getChartColors,
    getChartColorAt,
    isMissingColor,
} from '../../utils/chartHelpers';
import type { ChartDataLike } from '../../utils/chartHelpers';
import { palette } from '../../constants/colors';
import ChartFrame from './ChartFrame';

const applyBarTheme = (data: ChartDataLike): ChartDataLike => {
    if (!data || !Array.isArray(data.datasets) || data.datasets.length === 0) return data;
    const labelsLength = Array.isArray(data.labels) ? data.labels.length : 0;
    const dsCount = data.datasets.length;
    const datasets = data.datasets.map((ds, i) => {
        const next: any = { ...ds };
        const explicitType = String(ds.type || '').toLowerCase();

        if (explicitType === 'line') {
            const c = palette.dark;
            if (isMissingColor(ds.borderColor)) next.borderColor = c;
            if (isMissingColor(ds.backgroundColor)) next.backgroundColor = c;
            if (isMissingColor(ds.pointBackgroundColor)) next.pointBackgroundColor = palette.white;
            if (isMissingColor(ds.pointBorderColor)) next.pointBorderColor = c;
            return next;
        }

        if (dsCount === 1 && labelsLength > 1) {
            if (isMissingColor(ds.backgroundColor)) {
                next.backgroundColor =
                    labelsLength > 8 ? palette.primary : getChartColors(labelsLength);
            }
        } else {
            const c = getChartColorAt(i);
            if (isMissingColor(ds.backgroundColor)) next.backgroundColor = c;
            if (isMissingColor(ds.borderColor)) next.borderColor = c;
        }
        return next;
    });
    return { ...data, datasets };
};

export interface BarChartProps {
    data: ChartDataLike;
    options?: any;
    className?: string;
    title?: string;
    badge?: string;
}

const BarChart: React.FC<BarChartProps> = ({ data, options, className, title, badge }) => {
    const merged = React.useMemo(() => deepMerge(barDefaults, options || {}), [options]);
    const themed = React.useMemo(() => applyBarTheme(data), [data]);
    return (
        <ChartFrame title={title} badge={badge} className={className}>
            <Bar data={themed as any} options={merged} />
        </ChartFrame>
    );
};

export default BarChart;
